import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import LanguageDetector from "i18next-browser-languagedetector";

import translationDE from "./assets/languages/de.json";
import translationEN from "./assets/languages/en.json";
import translationIT from "./assets/languages/it.json";
import translationFR from "./assets/languages/fr.json";

const resources = {
	en: {
		translation: translationEN,
	},
	de: {
		translation: translationDE,
	},
	it: {
		translation: translationIT,
	},
	fr: {
		translation: translationFR,
	},
};

/**
 * The four languages the UI is translated into. Exported so the callers that
 * have to validate a language code themselves — the URL parameter below, the
 * anonymous language switcher — stay in step with what i18next accepts.
 */
export const SUPPORTED_LANGUAGES = ["en", "de", "fr", "it"] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

/**
 * Narrows any language tag (a URL parameter, a browser setting, a stored
 * preference) to one of SUPPORTED_LANGUAGES, or undefined when it is none of
 * them. Regional tags collapse to their base language (fr-CH -> fr), matching
 * what i18next's own `supportedLngs` resolution does.
 */
export const toSupportedLanguage = (
	value?: string | null,
): SupportedLanguage | undefined => {
	const base = value?.split("-")[0]?.toLowerCase();
	return SUPPORTED_LANGUAGES.find((language) => language === base);
};

/**
 * Query parameters that pick the UI language explicitly, e.g. the BMS entry
 * point /configurator?src=bms&lang=fr. `lng` is i18next's own convention and
 * `lang` the one that reads naturally in a partner link — both are accepted so
 * nobody has to remember which spelling this app wants.
 */
export const LANGUAGE_QUERY_PARAMS = ["lang", "lng"] as const;

/**
 * The parameter a manual language switch writes back into the URL (see
 * AnonymousHeader), when the URL does not already carry one of the others.
 */
export const LANGUAGE_QUERY_PARAM = LANGUAGE_QUERY_PARAMS[0];

/**
 * Reads the language out of the URL. Registered ahead of every built-in
 * detector in `detection.order`, so an explicit parameter outranks both the
 * localStorage cache from an earlier visit and the browser's own setting —
 * which is the point of handing out a link with the language baked in.
 *
 * Unsupported values (?lang=es) look up as undefined rather than as "es", so
 * detection falls through to the browser setting instead of resolving a
 * language we do not have to the `en` fallback.
 */
const urlLanguageDetector = {
	name: "urlLanguage",
	lookup: (): string | undefined => {
		if (typeof window === "undefined") return undefined;
		const params = new URLSearchParams(window.location.search);
		return LANGUAGE_QUERY_PARAMS.map((param) =>
			toSupportedLanguage(params.get(param)),
		).find(Boolean);
	},
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(urlLanguageDetector);

i18n
	// load translation using http -> see /public/locales (i.e. https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
	// learn more: https://github.com/i18next/i18next-http-backend
	//   .use(Backend)
	// detect user language
	// learn more: https://github.com/i18next/i18next-browser-languageDetector
	.use(languageDetector)
	// pass the i18n instance to react-i18next.
	.use(initReactI18next) // passes i18n down to react-i18next
	.init({
		fallbackLng: "en",
		resources,
		cleanCode: true,
		supportedLngs: [...SUPPORTED_LANGUAGES],
		detection: {
			// Same as the detector's own default order, with the URL parameter
			// put in front of it. The resolved language is still cached in
			// localStorage (the detector's default `caches`), so it survives a
			// visit to a URL that carries no parameter at all.
			order: [
				"urlLanguage",
				"querystring",
				"cookie",
				"localStorage",
				"sessionStorage",
				"navigator",
				"htmlTag",
			],
		},
		// keySeparator: false, // we do not use keys in form messages.welcome
		interpolation: {
			prefix: "*",
			suffix: "*",
			escapeValue: false, // react already safes from xss
		},
		// debug: true,
	});

export default i18n;
